/**
 * Accessibility enhancements
 * European Accessibility Act compliance
 */

// Detect keyboard navigation
let isKeyboardUser = false;

document.addEventListener('keydown', (e) => {
  if (e.key === 'Tab') {
    isKeyboardUser = true;
    document.body.classList.add('keyboard-user');
  }
});

document.addEventListener('mousedown', () => {
  isKeyboardUser = false;
  document.body.classList.remove('keyboard-user');
});

// Focus trap for modal dialogs
export function trapFocus(element) {
  const focusableElements = element.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  element.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  });

  // Focus first element when trap is activated
  firstFocusable?.focus();
}

// Announce to screen readers
export function announce(message, priority = 'polite') {
  const announcer = document.createElement('div');
  announcer.setAttribute('role', 'status');
  announcer.setAttribute('aria-live', priority);
  announcer.setAttribute('aria-atomic', 'true');
  announcer.className = 'sr-only';
  announcer.textContent = message;

  document.body.appendChild(announcer);

  setTimeout(() => {
    document.body.removeChild(announcer);
  }, 1000);
}

// Enhanced skip link functionality
document.addEventListener('DOMContentLoaded', () => {
  const skipLinks = document.querySelectorAll('.skip-link');

  skipLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      const targetId = link.getAttribute('href').slice(1);
      const target = document.getElementById(targetId);

      if (target) {
        e.preventDefault();
        target.setAttribute('tabindex', '-1');
        target.focus();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Remove tabindex after focus
        target.addEventListener('blur', () => {
          target.removeAttribute('tabindex');
        }, { once: true });
      }
    });
  });
});

// Respect prefers-reduced-motion
export function shouldReduceMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Add keyboard shortcuts help
document.addEventListener('keydown', (e) => {
  // Press '?' to show keyboard shortcuts
  if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
    const activeElement = document.activeElement;
    const isInputField = activeElement.tagName === 'INPUT' ||
                        activeElement.tagName === 'TEXTAREA' ||
                        activeElement.isContentEditable;

    if (!isInputField) {
      e.preventDefault();
      showKeyboardShortcuts();
    }
  }
});

function showKeyboardShortcuts() {
  announce('Keyboard shortcuts: Tab to navigate, Enter to activate, Escape to close menus, Arrow keys for sliders and maps', 'assertive');
}

// Enhance form accessibility
document.addEventListener('DOMContentLoaded', () => {
  // Add aria-describedby to form fields with errors
  const errorMessages = document.querySelectorAll('[role="alert"], .error-message');
  errorMessages.forEach((error, index) => {
    const errorId = `error-${index}`;
    error.id = errorId;

    // Find associated input
    const container = error.closest('.field, .form-group, [class*="field"]');
    if (container) {
      const input = container.querySelector('input, select, textarea');
      if (input) {
        const existingDescribedBy = input.getAttribute('aria-describedby');
        input.setAttribute(
          'aria-describedby',
          existingDescribedBy ? `${existingDescribedBy} ${errorId}` : errorId
        );
      }
    }
  });

  // Enhance required fields with aria-required if not already set
  const requiredFields = document.querySelectorAll('[required]');
  requiredFields.forEach(field => {
    if (!field.hasAttribute('aria-required')) {
      field.setAttribute('aria-required', 'true');
    }
  });
});

// Improve carousel/slider accessibility
export function makeCarouselAccessible(carouselElement) {
  const slides = carouselElement.querySelectorAll('.swiper-slide');
  const prevButton = carouselElement.querySelector('.swiper-button-prev');
  const nextButton = carouselElement.querySelector('.swiper-button-next');
  const pagination = carouselElement.querySelector('.swiper-pagination');

  // Set ARIA roles
  carouselElement.setAttribute('role', 'region');
  carouselElement.setAttribute('aria-label', 'Carousel');

  if (prevButton) {
    prevButton.setAttribute('aria-label', 'Previous slide');
  }

  if (nextButton) {
    nextButton.setAttribute('aria-label', 'Next slide');
  }

  // Make slides accessible
  slides.forEach((slide, index) => {
    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-roledescription', 'slide');
    slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`);
  });

  // Make pagination accessible
  if (pagination) {
    pagination.setAttribute('role', 'tablist');
    const bullets = pagination.querySelectorAll('.swiper-pagination-bullet');
    bullets.forEach((bullet, index) => {
      bullet.setAttribute('role', 'tab');
      bullet.setAttribute('aria-label', `Go to slide ${index + 1}`);
    });
  }
}

// Apply to all Swiper instances after initialization
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    const carousels = document.querySelectorAll('.swiper');
    carousels.forEach(makeCarouselAccessible);
  }, 500);
});

// Handle dynamic content updates
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
      // Check for new carousels
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          if (node.classList?.contains('swiper')) {
            makeCarouselAccessible(node);
          }

          // Check children
          const carousels = node.querySelectorAll?.('.swiper');
          carousels?.forEach(makeCarouselAccessible);
        }
      });
    }
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
});

// Export utilities
export default {
  trapFocus,
  announce,
  shouldReduceMotion,
  makeCarouselAccessible,
};
